import { useForm } from 'vee-validate'
import { onBeforeRouteLeave } from 'vue-router'
import { useConfirmation } from './useConfirmation'

import type { TypedSchema, FormContext } from 'vee-validate'

export function useVrifyForm<
  TValues extends Record<string, any> = Record<string, any>,
  TOutput extends Record<string, any> = TValues,
  TSchema extends TypedSchema<TValues, TOutput> | undefined = undefined,
>(
  formOptions: Parameters<typeof useForm<TValues, TOutput, TSchema>>[0],
  confirmCallback?: () => Promise<boolean>,
  enableConfirmation: boolean = true,
): FormContext<TValues, TOutput> & { isConfirmationEnabled: typeof isConfirmationEnabled } {
  const form = useForm<TValues, TOutput, TSchema>(formOptions)
  const isConfirmationEnabled = ref(enableConfirmation)
  const wasSubmitted = ref(false)
  const originalHandleSubmit = form.handleSubmit

  onMounted(() => {
    window.addEventListener('beforeunload', handleBeforeUnload)
  })

  onUnmounted(() => {
    window.removeEventListener('beforeunload', handleBeforeUnload)
  })

  onBeforeRouteLeave(async (_to, _from, next) => {
    if (shouldBypassConfirmation()) {
      next()
      return
    }

    const confirmed = await handleConfirmation()
    next(confirmed ? undefined : false)
  })

  form.handleSubmit = ((cb: Parameters<typeof form.handleSubmit>[0]) => {
    wasSubmitted.value = false
    const wrapped = originalHandleSubmit(cb)

    return async () => {
      const result = await wrapped()
      if (form.meta.value.valid) wasSubmitted.value = true
      return result
    }
  }) as typeof form.handleSubmit

  function handleBeforeUnload(event: BeforeUnloadEvent) {
    if (!shouldBypassConfirmation()) {
      event.preventDefault()
      event.returnValue = ''
    }
  }

  function shouldBypassConfirmation(): boolean {
    return (
      !(form.meta.value.dirty && form.meta.value.touched) || !isConfirmationEnabled.value || form.isSubmitting.value || wasSubmitted.value
    )
  }

  async function handleConfirmation(): Promise<boolean> {
    if (confirmCallback) return await confirmCallback()

    return await useConfirmation().confirmation({
      title: 'Unsaved Changes',
      description: 'You have unsaved changes that will be lost. Are you sure you want to continue?',
      confirmText: 'Continue',
      cancelText: 'Cancel',
      requireConfirmation: false,
      variant: 'default',
    })
  }

  return { ...form, isConfirmationEnabled }
}
